'use client'

import { cn } from '@/utilities/ui'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp, Search } from 'lucide-react'
import * as React from 'react'

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    ;(ref as { current: T | null }).current = value
  }
}

type SelectProps = React.ComponentProps<typeof SelectPrimitive.Root>

const SelectOpenContext = React.createContext(false)

const useSelectOpen = () => React.useContext(SelectOpenContext)

const Select: React.FC<SelectProps> = ({ children, onOpenChange, open, ...props }) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const isControlled = open !== undefined
  const isOpen = isControlled ? open : uncontrolledOpen

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(nextOpen)
      }
      onOpenChange?.(nextOpen)
    },
    [isControlled, onOpenChange],
  )

  return (
    <SelectOpenContext.Provider value={isOpen}>
      <SelectPrimitive.Root open={open} onOpenChange={handleOpenChange} {...props}>
        {children}
      </SelectPrimitive.Root>
    </SelectOpenContext.Provider>
  )
}

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger: React.FC<
  { ref?: React.Ref<HTMLButtonElement> } & React.ComponentProps<typeof SelectPrimitive.Trigger>
> = ({ children, className, ref, ...props }) => (
  <SelectPrimitive.Trigger
    className={cn(
      'flex h-10 w-full cursor-pointer items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-inherit ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className,
    )}
    ref={ref}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
)

const SelectScrollUpButton: React.FC<
  { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>
> = ({ className, ref, ...props }) => (
  <SelectPrimitive.ScrollUpButton
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    ref={ref}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
)

const SelectScrollDownButton: React.FC<
  { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<
    typeof SelectPrimitive.ScrollDownButton
  >
> = ({ className, ref, ...props }) => (
  <SelectPrimitive.ScrollDownButton
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    ref={ref}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
)

type SelectSearchInputProps = {
  onValueChange?: (value: string) => void
  containerClassName?: string
  autoFocusOnMount?: boolean
  testId?: string
} & Omit<React.ComponentProps<'input'>, 'ref' | 'onChange'>

const SelectSearchInput = React.forwardRef<HTMLInputElement, SelectSearchInputProps>(
  function SelectSearchInput(
    {
      className,
      containerClassName,
      onKeyDown,
      onValueChange,
      autoFocusOnMount = true,
      testId = 'select-search-input',
      ...props
    },
    forwardedRef,
  ) {
    const localInputRef = React.useRef<HTMLInputElement>(null)

    const setInputRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        localInputRef.current = node
        assignRef(forwardedRef, node)
      },
      [forwardedRef],
    )

    React.useEffect(() => {
      if (!autoFocusOnMount || props.disabled || props.readOnly) {
        return
      }

      const frameId = window.requestAnimationFrame(() => {
        localInputRef.current?.focus()
      })

      return () => window.cancelAnimationFrame(frameId)
    }, [autoFocusOnMount, props.disabled, props.readOnly])

    return (
      <div
        className={cn(
          'relative w-full border-b border-tenant-border bg-tenant-card-background px-4 py-1.5 rounded-t-md',
          'focus-within:border-b-tenant-primary-action-color/25',
          containerClassName,
        )}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="pointer-events-none absolute inset-y-0 left-5 flex items-center">
          <Search className="h-4 w-4" aria-hidden />
        </div>
        <input
          className={cn(
            'h-11 w-full border-0 bg-transparent py-2 pl-12 pr-3 text-base',
            'placeholder:text-placeholder',
            'outline-none ring-0 ring-offset-0 shadow-none',
            'focus:outline-none focus:ring-0 focus:ring-offset-0 focus:shadow-none',
            'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-none',
            className,
          )}
          onChange={(event) => onValueChange?.(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
              event.stopPropagation()
            }
            onKeyDown?.(event)
          }}
          data-testid={testId}
          ref={setInputRef}
          {...props}
        />
      </div>
    )
  },
)

SelectSearchInput.displayName = 'SelectSearchInput'

const SelectContent: React.FC<
  {
    ref?: React.Ref<HTMLDivElement>
    scrollUI?: 'arrows' | 'none' | 'scrollbar'
    search?: React.ReactNode
  } & React.ComponentProps<typeof SelectPrimitive.Content>
> = ({ children, className, position = 'popper', ref, scrollUI = 'arrows', search, ...props }) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        'relative z-50 flex max-h-96 min-w-32 flex-col overflow-hidden rounded border bg-card text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      ref={ref}
      {...props}
    >
      {search}
      {scrollUI === 'arrows' ? <SelectScrollUpButton /> : null}
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)',
          scrollUI === 'scrollbar' && 'custom-scrollbar',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      {scrollUI === 'arrows' ? <SelectScrollDownButton /> : null}
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
)

type SelectSearchableOption = {
  value: string
  label: string
}

const SelectSearchableContent: React.FC<
  {
    options: SelectSearchableOption[]
    selectedValue?: string
    onSearchQueryChange?: (query: string) => void
    searchPlaceholder?: string
    searchAriaLabel?: string
    noResults?: React.ReactNode
    includeSelectedWhenFilteredOut?: boolean
    itemClassName?: string
  } & Omit<React.ComponentProps<typeof SelectContent>, 'children' | 'search'>
> = ({
  options,
  selectedValue,
  onSearchQueryChange,
  searchPlaceholder = 'Search...',
  searchAriaLabel = 'Search options',
  noResults,
  includeSelectedWhenFilteredOut = true,
  itemClassName,
  ...contentProps
}) => {
  const isOpen = useSelectOpen()
  const [searchQuery, setSearchQuery] = React.useState('')
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
    }
  }, [isOpen])

  const filteredOptions = React.useMemo(() => {
    const normalizedSearchQuery = searchQuery.trim().toLowerCase()
    if (!normalizedSearchQuery) {
      return options
    }

    const matchedOptions = options.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearchQuery),
    )

    if (!includeSelectedWhenFilteredOut || !selectedValue) {
      return matchedOptions
    }

    const selectedAlreadyPresent = matchedOptions.some((option) => option.value === selectedValue)
    if (selectedAlreadyPresent) {
      return matchedOptions
    }

    const selectedOption = options.find((option) => option.value === selectedValue)
    return selectedOption ? [selectedOption, ...matchedOptions] : matchedOptions
  }, [includeSelectedWhenFilteredOut, options, searchQuery, selectedValue])

  return (
    <SelectContent
      search={
        <SelectSearchInput
          ref={searchInputRef}
          type="text"
          role="searchbox"
          aria-label={searchAriaLabel}
          value={searchQuery}
          placeholder={searchPlaceholder}
          onValueChange={(value) => {
            setSearchQuery(value)
            onSearchQueryChange?.(value)
          }}
        />
      }
      {...contentProps}
    >
      {filteredOptions.length > 0
        ? filteredOptions.map((option, index) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className={itemClassName}
              {...(index === 0 || index === filteredOptions.length - 1
                ? {
                    // If the first option is highlighted and the user presses the arrow up key
                    // or the last option is highlighted and the user presses the arrow down key, focus the search input
                    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
                      if (
                        (event.key === 'ArrowUp' && index === 0) ||
                        (event.key === 'ArrowDown' && index === filteredOptions.length - 1)
                      ) {
                        event.preventDefault()
                        event.stopPropagation()
                        searchInputRef.current?.focus()
                      }
                    },
                  }
                : {})}
            >
              {option.label}
            </SelectItem>
          ))
        : (noResults ?? <div className="px-3 py-2 text-sm">No options found</div>)}
    </SelectContent>
  )
}

const SelectLabel: React.FC<
  { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<typeof SelectPrimitive.Label>
> = ({ className, ref, ...props }) => (
  <SelectPrimitive.Label
    className={cn('py-1.5 pl-8 pr-2 text-sm font-medium', className)}
    ref={ref}
    {...props}
  />
)

const SelectItem: React.FC<
  { ref?: React.Ref<HTMLDivElement>; value: string } & React.ComponentProps<
    typeof SelectPrimitive.Item
  >
> = ({ children, className, ref, ...props }) => (
  <SelectPrimitive.Item
    className={cn(
      'relative flex w-full cursor-pointer select-none items-center rounded py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50',
      className,
    )}
    ref={ref}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
)

const SelectSeparator: React.FC<
  { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<typeof SelectPrimitive.Separator>
> = ({ className, ref, ...props }) => (
  <SelectPrimitive.Separator
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    ref={ref}
    {...props}
  />
)

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSearchableContent,
  SelectSearchInput,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
